import axios, { AxiosInstance } from "axios";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "..";
import { baseUrl } from "../hooks/useHttpClient";
import { LoginResult, useBerichtsheft } from "./BerichtsheftContext";
import useAuthStore from "./useAuthStore";

export function LoginValidator({ children }: React.PropsWithChildren) {
  const {
    accessToken,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    setUser,
  } = useAuthStore();

  const navivate = useNavigate();

  const selectedYear = useBerichtsheft((state) => state.selectedYear);
  const setSelectedYear = useBerichtsheft((state) => state.setSelectedYear);
  const { pathname } = useLocation();

  const requiresAuthentication = useMemo(
    () =>
      routes.protectedRoutes.some((route) => route.path.startsWith(pathname)),
    [pathname]
  );

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const handleSuccessfulAuthentication = useCallback(
    (data: LoginResult) => {
      setAccessToken(data.tokenPair.accessToken);
      setRefreshToken(data.tokenPair.refreshToken);
      setUser(data.user);

      if (!selectedYear) {
        setSelectedYear(data.user.startDate);
      }

      setIsAuthenticated(true);
    },
    [selectedYear, setSelectedYear, setAccessToken, setRefreshToken, setUser]
  );

  const handleFailedAuthentication = useCallback(() => {
    localStorage.clear();
    if (requiresAuthentication) {
      navivate("/login", { replace: true });
    }
  }, [requiresAuthentication]);

  const refreshAccessToken = useCallback(
    async (client: AxiosInstance) => {
      try {
        const response = await client.post<LoginResult>(
          "/protected/refreshAccessToken",
          {},
          {
            headers: {
              authorization: `Bearer ${refreshToken}`,
            },
          }
        );
        handleSuccessfulAuthentication(response.data);
      } catch (error) {
        handleFailedAuthentication();
      }
    },
    [refreshToken, handleSuccessfulAuthentication, handleFailedAuthentication]
  );

  const validateAccessToken = useCallback(
    async (client: AxiosInstance) => {
      try {
        const response = await client.post<LoginResult>(
          "/protected/validateAccessToken",
          {},
          {
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          }
        );
        handleSuccessfulAuthentication(response.data);
      } catch (error) {
        await refreshAccessToken(client);
      }
    },
    [accessToken, handleSuccessfulAuthentication, refreshAccessToken]
  );

  useEffect(() => {
    const authenticate = async () => {
      if (!requiresAuthentication) {
        setIsAuthenticated(true);
        return;
      }
      const client = axios.create({ baseURL: baseUrl });
      await validateAccessToken(client);
    };

    authenticate();
  }, [requiresAuthentication, validateAccessToken]);

  if (requiresAuthentication && !isAuthenticated) {
    return (
      <div className="flex w-full justify-center items-center h-32">
        <button className="btn btn-square loading"></button>
      </div>
    );
  }

  return <Suspense fallback={<></>}>{children}</Suspense>;
}
